/* eslint-disable @typescript-eslint/member-ordering */
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { TemplatePortal } from '@angular/cdk/portal';
import {
   AfterViewInit,
   ChangeDetectorRef,
   Directive,
   ElementRef,
   EventEmitter,
   InjectionToken,
   Input,
   Output,
   QueryList,
   TemplateRef,
   ViewChild,
   ViewChildren,
   ViewContainerRef,
} from '@angular/core';
import { Observable, Subject, UnaryFunction, pipe } from 'rxjs';
import { map } from 'rxjs/operators';
import { LcsOptionComponent } from '../select/option/option.component';
import { LcsSingleSelectPanelDirective } from './single-select-panel.directive';

let nextUniqueId = 0;

/** Event object that is emitted when an autocomplete option is selected. */
export class LcsSingleSelectSelectedEvent<T = any> {
   constructor(
      /** Reference to the autocomplete panel that emitted the event. */
      public source: LcsSingleSelect<T>,
      /** Option that was selected. */
      public option: LcsOptionComponent<T>
   ) {}
}

export const LCS_SINGLE_SELECT_OPTIONS = new InjectionToken<LcsSingleSelect<any>>('lcs-single-select-options');

export interface LcsSingleSelect<TItem> {
   panel: LcsSingleSelectPanelDirective;

   options: QueryList<LcsOptionComponent<TItem>>;

   optionsChange: Subject<TItem[]>;

   closed: EventEmitter<never>;

   opened: EventEmitter<never>;

   optionSelected: EventEmitter<LcsSingleSelectSelectedEvent>;

   portal: TemplatePortal;

   excludedIds: number[];

   excludedIdSet: Set<number>;

   id: string;

   isOpen: boolean;

   keyManager: ActiveDescendantKeyManager<LcsOptionComponent<TItem>>;

   isPanelVisible: boolean;

   autoActiveFirstOption: boolean;

   isIncludeEmptyOption: boolean;

   displayWith(item: TItem | null): string | null;

   setScrollTop(scrollTop: number): void;

   getScrollTop(): number;

   setVisibility(): void;

   emitSelectEvent(option: LcsOptionComponent): void;

   trackBy(item: TItem | null): any;
}

@Directive({
    standalone: false
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class LcsSingleSelectBase<TItem> implements AfterViewInit, LcsSingleSelect<TItem> {
   @Input() excludedValues: any[];

   @Input()
   public set excludedIds(value: number[]) {
      if (value) {
         this.excludedIdSet = new Set(value);
      }
   }

   public excludedIdSet: Set<number>;

   @Input()
   set isIncludeEmptyOption(includeEmptyOption: boolean) {
      this._isIncludeEmptyOption = coerceBooleanProperty(includeEmptyOption);
   }

   get isIncludeEmptyOption(): boolean {
      return this._isIncludeEmptyOption;
   }

   @Input()
   set overrideEmptyOption(overrideEmptyOption: string) {
      this._overrideEmptyOption = overrideEmptyOption;
   }

   get overrideEmptyOption(): string {
      return this._overrideEmptyOption;
   }

   // eslint-disable-next-line @typescript-eslint/naming-convention
   private _isIncludeEmptyOption = false;

   // eslint-disable-next-line @typescript-eslint/naming-convention
   private _overrideEmptyOption = '';

   /**
    * Whether the first option should be highlighted when the autocomplete panel is opened.
    */
   @Input()
   set autoActiveFirstOption(value: boolean) {
      this._autoActiveFirstOption = coerceBooleanProperty(value);
   }

   get autoActiveFirstOption(): boolean {
      return this._autoActiveFirstOption;
   }

   // eslint-disable-next-line @typescript-eslint/naming-convention
   private _autoActiveFirstOption = true;

   @ViewChild(LcsSingleSelectPanelDirective) panel: LcsSingleSelectPanelDirective;

   @ViewChild(TemplateRef) template: TemplateRef<any>;

   @ViewChildren(LcsOptionComponent) set viewChildrenOptions(value: QueryList<LcsOptionComponent<TItem>>) {
      if (!this.options) {
         this.options = value;
      }
   }

   @Output() closed = new EventEmitter<never>();

   @Output() opened = new EventEmitter<never>();

   @Output() optionSelected = new EventEmitter<LcsSingleSelectSelectedEvent>();

   portal: TemplatePortal;

   /** Unique ID to be used by autocomplete trigger's "aria-owns" property. */
   id = `lcs-single-select-${++nextUniqueId}`;

   isOpen = false;

   keyManager: ActiveDescendantKeyManager<LcsOptionComponent<TItem>>;

   isPanelVisible = false;

   options: QueryList<LcsOptionComponent<TItem>>;

   optionsChange: Subject<TItem[]>;

   constructor(
      protected elementRef: ElementRef,
      protected changeDetectorRef: ChangeDetectorRef,
      protected viewContainerRef: ViewContainerRef
   ) {}

   ngAfterViewInit(): void {
      this.portal = new TemplatePortal(this.template, this.viewContainerRef);
      this.keyManager = new ActiveDescendantKeyManager<LcsOptionComponent<TItem>>(this.options).withWrap();
      this.setVisibility();
   }

   /** Emits the `select` event. */
   emitSelectEvent(option: LcsOptionComponent): void {
      const event = new LcsSingleSelectSelectedEvent(this, option);
      this.optionSelected.emit(event);
   }

   getScrollTop(): number {
      return this.panel ? this.panel.elementRef.nativeElement.scrollTop : 0;
   }

   setScrollTop(scrollTop: number): void {
      if (this.panel) {
         this.panel.elementRef.nativeElement.scrollTop = scrollTop;
      }
   }

   setVisibility(): void {
      this.isPanelVisible = this._isIncludeEmptyOption ? this.options.length > 1 : this.options.length > 0;
      this.changeDetectorRef.markForCheck();
   }

   abstract displayWith(item: TItem | null): string | null;

   abstract trackBy(item: TItem | null): any;

   protected itemsLoaded(): UnaryFunction<Observable<TItem[]>, Observable<TItem[]>> {
      return pipe(
         map((items: TItem[]) => {
            if (!this.excludedIdSet && !this.excludedValues) {
               return items;
            }
            return items.filter((item) => {
               const id = this.trackBy(item);
               if (id == null) {
                  return false;
               }
               const notInExcludedIds = !this.excludedIdSet?.has(+id);
               const notInExcludedValues = this.excludedValues ? this.excludedValues.indexOf(id) === -1 : true;

               return notInExcludedIds && notInExcludedValues;
            });
         })
      );
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   static ngAcceptInputType_includeEmptyOption: BooleanInput;

   static ngAcceptInputType_autoActiveFirstOption: BooleanInput;
}
